import { cn } from "@/lib/utils/cn";
import { Loader2 } from "lucide-react";
import { ReactNode, Suspense } from "react";
import { Skeleton } from "./skeleton";

// --- Types ---

interface LoadingFallbackProps {
  text?: string;
  className?: string;
  variant?: "spinner" | "skeleton" | "minimal";
}

// --- Components ---

/**
 * Modern, minimalist loading state inspired by high-end SaaS products.
 */
const ModernLoadingFallback = ({ 
  text = "Lädt...", 
  className,
  variant = "spinner" 
}: LoadingFallbackProps) => {
  if (variant === "skeleton") {
    return (
      <div className={cn("w-full h-screen p-6 space-y-6 animate-in fade-in duration-500", className)}>
        <div className="flex items-center space-x-4">
          <Skeleton className="h-12 w-12 rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-[200px]" />
            <Skeleton className="h-4 w-[150px]" />
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-[240px] w-full rounded-2xl" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-[90%]" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "minimal") {
    return (
      <div className={cn("flex items-center justify-center h-screen p-4 gap-3 animate-in fade-in duration-300", className)}>
        <Loader2 className="size-4 animate-spin text-primary" />
        <span className="text-xs font-medium text-muted-foreground">{text}</span>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex flex-col items-center justify-center h-screen w-full p-8 animate-in fade-in zoom-in-95 duration-500", 
      className
    )}>
      <div className="relative flex items-center justify-center mb-6">
        {/* Subtle background glow */}
        <div className="absolute inset-0 size-16 rounded-full bg-primary/5 blur-2xl animate-pulse" />
        
        {/* Main Loader UI */}
        <div className="relative flex size-12 items-center justify-center rounded-2xl bg-white border border-border shadow-sm ring-1 ring-black/[0.02]">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      </div>
      
      {text && (
        <div className="flex flex-col items-center gap-1">
          <p className="text-sm font-semibold text-foreground tracking-tight">
            {text}
          </p>
          <p className="text-xs text-muted-foreground/60">
            Einen Augenblick bitte...
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Backward compatibility: Blue Loading Fallback
 */
export const BlueLoadingFallback = ({ text = "Lädt..." }: { text?: string }) => (
  <ModernLoadingFallback text={text} />
);

/**
 * Backward compatibility: Green Loading Fallback
 */
export const GreenLoadingFallback = ({ text = "Lädt..." }: { text?: string }) => (
  <ModernLoadingFallback text={text} />
);

interface SuspenseWrapperProps {
  children?: ReactNode;
  fallback?: ReactNode;
  /** Visual variant of the loading state */
  variant?: "spinner" | "skeleton" | "minimal";
  /** @deprecated use variant instead */
  type?: "blue" | "green";
  /** Optional text to display */
  text?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * A reusable Suspense component with a default modern loading fallback.
 * Automatically aligns with the project's minimalist SaaS design language.
 */
export const SuspenseWrapper = ({ 
  children, 
  fallback, 
  variant = "spinner",
  text = "Lädt...",
  className
}: SuspenseWrapperProps) => {
  return (
    <Suspense fallback={fallback || <ModernLoadingFallback text={text} variant={variant} className={className} />}>
      {children ?? <></>}
    </Suspense>
  );
};
